import { CSSProperties } from 'react';

export interface StyleProps {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  userMessageColor?: string;
  userMessageTextColor?: string;
  botMessageColor?: string;
  botMessageTextColor?: string;
  fontFamily?: string;
  fontSize?: string;
  borderRadius?: string;
  headerText?: string;
  roundedChatButton?: boolean;
}

// Default style values
export const getDefaultStyles = (): StyleProps => ({
  primaryColor: '#000',
  secondaryColor: '#5471c6',
  backgroundColor: '#ffffff',
  userMessageColor: '#e3effd',
  userMessageTextColor: '#000000',
  botMessageColor: '#f5f5f5',
  botMessageTextColor: '#333333',
  fontFamily: 'Arial, sans-serif',
  fontSize: '15.2px',
  borderRadius: '16px',
  headerText: 'Conversatie',
  roundedChatButton: true,
});

// App container styles
export const getAppContainerStyle = (): CSSProperties => ({
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  zIndex: 9000
});

// Chat container styles
export const getChatContainerStyle = (style: StyleProps): CSSProperties => {
  const { fontFamily, fontSize } = style;
  
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'relative',
    fontFamily,
    fontSize,
  };
};

// Chat button styles
export const getChatButtonStyle = (style: StyleProps, isOpen?: boolean): CSSProperties => {
  const { primaryColor, borderRadius, roundedChatButton } = style;
  
  // Convert hex color to rgba for the pulse effect
  const hexToRgba = (hex: string, alpha: number) => {
    // Handle both hex with # and without #
    const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;
    
    // Handle 3-digit hex codes
    let r, g, b;
    if (cleanHex.length === 3) {
      r = parseInt(cleanHex[0] + cleanHex[0], 16);
      g = parseInt(cleanHex[1] + cleanHex[1], 16);
      b = parseInt(cleanHex[2] + cleanHex[2], 16);
    } else {
      r = parseInt(cleanHex.slice(0, 2), 16);
      g = parseInt(cleanHex.slice(2, 4), 16);
      b = parseInt(cleanHex.slice(4, 6), 16);
    }
    
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  const baseBoxShadow = !isOpen ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.15)';
  
  return {
    cursor: 'pointer',
    width: '56px',
    height: '56px',
    background: primaryColor,
    borderRadius: roundedChatButton ? '50%' : borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: baseBoxShadow,
    transition: 'all 0.2s ease',
    position: 'relative',
    animation: !isOpen ? 'pulse 1.5s infinite' : 'none',
    // Set CSS custom properties for the pulse animation
    ['--pulse-color' as any]: hexToRgba(primaryColor || '#5a99d4', 0.5),
    ['--pulse-color-transparent' as any]: hexToRgba(primaryColor || '#5a99d4', 0),
  };
};

// Icon base styles
export const getIconBaseStyle = (): CSSProperties => ({
  cursor: 'pointer',
  width: '32px',
  height: '32px',
  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  position: 'absolute',
});

// Animation classes for entering and exiting icons
export const getIconAnimationStyle = (isAnimating: boolean): CSSProperties => {
  if (isAnimating) {
    return {
      ...getIconBaseStyle(),
      opacity: 0,
      transform: 'translateY(30px) rotate(90deg)'
    };
  }
  return {
    ...getIconBaseStyle(),
    opacity: 1,
    transform: 'translateY(0) rotate(0deg)',
    animation: 'pop-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards'
  };
};

// Chat content styles
export const getChatContentStyle = (style: StyleProps): CSSProperties => {
  const { backgroundColor, borderRadius, fontFamily, fontSize } = style;
  
  return {
    position: 'absolute',
    bottom: '70px',
    right: '0',
    width: '560px',
    height: '600px',
    maxHeight: '75vh',
    backgroundColor,
    borderRadius,
    boxShadow: '0 5px 25px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    fontFamily,
    fontSize,
  };
};

// Header styles
export const getHeaderStyle = (style: StyleProps): CSSProperties => {
  const { primaryColor, borderRadius } = style;
  
  return {
    padding: '16px',
    background: primaryColor,
    color: 'white',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopLeftRadius: borderRadius,
    borderTopRightRadius: borderRadius,
  };
};

// Header title style
export const getHeaderTitleStyle = (): CSSProperties => ({
  margin: '0',
  fontSize: '15.2px',
  fontWeight: 600,
});

// Close button style
export const getCloseButtonStyle = (): CSSProperties => ({
  background: 'none',
  border: 'none',
  color: 'white',
  cursor: 'pointer',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  transition: 'background 0.2s ease',
});

// Messages container style
export const getMessagesContainerStyle = (style: StyleProps): CSSProperties => {
  const { backgroundColor } = style;
  
  return {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor,
  };
};

// Empty state style
export const getEmptyStateStyle = (): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: '#888',
  fontSize: '14.4px',
});

// Message base style
export const getMessageBaseStyle = (): CSSProperties => ({
  padding: '10px 14px',
  maxWidth: '100%',
  wordBreak: 'break-word',
  lineHeight: 1.4,
  fontSize: '14.44px',
  position: 'relative',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
});

// Bot message style
export const getBotMessageStyle = (style: StyleProps): CSSProperties => {
  const { botMessageColor, botMessageTextColor, borderRadius } = style;
  
  return {
    ...getMessageBaseStyle(),
    alignSelf: 'flex-start',
    backgroundColor: botMessageColor,
    color: botMessageTextColor,
    borderRadius,
    // borderTopLeftRadius: '4px',
  };
};

// User message style
export const getUserMessageStyle = (style: StyleProps): CSSProperties => {
  const { userMessageColor, userMessageTextColor, borderRadius } = style;
  
  return {
    ...getMessageBaseStyle(),
    alignSelf: 'flex-end',
    backgroundColor: userMessageColor,
    color: userMessageTextColor,
    borderRadius,
    // borderTopRightRadius: '4px',
  };
};

// Message content style
export const getMessageContentStyle = (): CSSProperties => ({
  marginBottom: '2px',
});

// Input container style
export const getInputContainerStyle = (style: StyleProps): CSSProperties => {
  const { backgroundColor } = style;
  
  return {
    padding: '12px 16px',
    borderTop: '1px solid #eaeaea',
    backgroundColor,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };
};

// Input style
export const getInputStyle = (style: StyleProps): CSSProperties => {
  const { borderRadius, fontFamily } = style;
  const radiusValue = typeof borderRadius === 'string' ? `calc(${borderRadius} / 1.5)` : '10px';
  
  return {
    flex: 1,
    padding: '10px 16px',
    border: '1px solid #e1e1e1',
    borderRadius: radiusValue,
    outline: 'none',
    fontSize: '13.68px',
    transition: 'all 0.2s ease',
    fontFamily,
  };
};

// Input focus style
export const getInputFocusStyle = (style: StyleProps): CSSProperties => {
  const { primaryColor } = style;
  
  return {
    boxShadow: `0 0 0 2px ${primaryColor}33`,
  };
};

// Send button style
export const getSendButtonStyle = (style: StyleProps, isDisabled: boolean): CSSProperties => {
  const { primaryColor } = style;
  
  const baseStyle: CSSProperties = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    background: isDisabled ? '#cccccc' : primaryColor,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0,
  };
  
  return baseStyle;
};

// Send button hover style
export const getSendButtonHoverStyle = (style: StyleProps): CSSProperties => {
  const { secondaryColor } = style;
  
  return {
    ...getSendButtonStyle(style, false),
    backgroundColor: secondaryColor,
    transform: 'scale(1.05)',
  };
};

// Carousel container style
export const getCarouselContainerStyle = (style: StyleProps): CSSProperties => {
  const { backgroundColor, primaryColor } = style;
  
  return {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    overflowX: 'auto',
    backgroundColor,
    borderBottom: `1px solid ${primaryColor}20`,
    scrollbarWidth: 'none' as 'none', // Firefox
    msOverflowStyle: 'none', // IE/Edge
    boxShadow: `0 2px 4px ${primaryColor}10`,
    zIndex: 1,
    gap: '8px',
  };
};

// Carousel item style
export const getCarouselItemStyle = (style: StyleProps, isActive: boolean): CSSProperties => {
  const { primaryColor, secondaryColor, borderRadius } = style;
  
  return {
    padding: '6px 14px',
    marginRight: '8px',
    borderRadius: borderRadius ? borderRadius.replace(/px$/, '').split(' ')[0] + 'px' : '16px',
    backgroundColor: isActive ? primaryColor : 'transparent',
    color: isActive ? '#fff' : secondaryColor || '#333',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as 'nowrap',
    fontSize: '12.92px',
    border: `1px solid ${isActive ? primaryColor : (secondaryColor || '#ccc')}`,
    transition: 'all 0.2s ease',
    flexShrink: 0,
  };
};

// Navigation button style
export const getCarouselNavButtonStyle = (style: StyleProps, direction: 'left' | 'right'): CSSProperties => {
  const { primaryColor } = style;
  
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: `${primaryColor}20`,
    color: primaryColor,
    cursor: 'pointer',
    flexShrink: 0,
    position: 'sticky',
    [direction === 'left' ? 'left' : 'right']: 0,
    zIndex: 2,
  };
}; 